import React from 'react';
import { cn } from '@/lib/utils';
import {
  Clock,
  Briefcase,
  Building2,
  Stethoscope,
  AlertTriangle,
  FileText,
  Info,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PremiumBadge } from '@/design-system/primitives/premium';
import { AppIcon } from '@/modules/shared/components/ui/AppIcon';
import { formatDisplayDate } from '../../utils/formatUtils';
import { formatWorkflowAction } from '@/modules/shared/utils/formatWorkflowAction';
import { useDensityContext } from '../../contexts/DensityContext';
import type { Patient360NavKey } from './PatientSidebarNav';
import type { Patient360ViewModel } from '../../types/patient360Model';
import type { DynamicEntityData } from '../../types/dynamicEntityView';
import type { PatientAlert } from '../../hooks/usePatientAlerts';
import { DynamicZoneRenderer } from '../dynamic-view/DynamicZoneRenderer';

// ─────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────

interface Patient360RightSidebarProps {
  vm: Patient360ViewModel;
  patientEntity?: DynamicEntityData;
  alerts?: PatientAlert[];
  className?: string;
  onNavigate?: (tab: Patient360NavKey) => void;
  onScrollTo?: (id: string) => void;
}

// ─────────────────────────────────────────────────────────────
// Small card wrapper used inside the sidebar
// ─────────────────────────────────────────────────────────────

function SideCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon?: LucideIcon;
  children: React.ReactNode;
}) {
  const { isCompact } = useDensityContext();
  return (
    <div className={cn(
      'rounded-xl border border-border-hairline bg-surface-premium/90 shadow-sm transition-colors hover:border-border-premium',
      isCompact ? 'p-2.5 space-y-1.5' : 'p-3 space-y-2',
    )}>
      <div className="flex items-center gap-1.5">
        {Icon && <AppIcon icon={Icon} size="compact" />}
        <span className="text-[11px] font-semibold text-foreground">{title}</span>
      </div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Row utility
// ─────────────────────────────────────────────────────────────

function MetaRow({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0">
        {Icon && <AppIcon icon={Icon} size="compact" className="opacity-80" />}
        {label}
      </div>
      <span className="text-[11px] text-foreground text-right break-words max-w-[60%]">
        {value || '—'}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

/**
 * Sticky right sidebar for Patient360 3-column layout.
 * Shows: Quick Metadata + Case KPIs + Financial KPIs + Workflow state.
 */
export const Patient360RightSidebar = React.memo(function Patient360RightSidebar({ vm, patientEntity, alerts = [], className, onNavigate, onScrollTo }: Patient360RightSidebarProps) {
  const { isCompact } = useDensityContext();
  const activeCase = vm.cases.find((caseItem) => caseItem.isActive) ?? vm.cases[0] ?? null;

  const timelineItems = React.useMemo(() => {
    const items: Array<{ id: string; label: string; detail: string; date: string | null; tab?: Patient360NavKey; scrollTargetId?: string }> = [];

    if (activeCase) {
      items.push({
        id: `case-${activeCase.caseId}`,
        label: 'Case updated',
        detail: activeCase.caseNumber || 'Active case',
        date: activeCase.updatedAt || activeCase.lastActivityAt || activeCase.createdAt,
        tab: 'cases',
      });
    }

    if (vm.calls[0]) {
      items.push({
        id: `call-${vm.calls[0].callLogId}`,
        label: 'Call logged',
        detail: vm.calls[0].callStatus || 'Patient interaction',
        date: vm.calls[0].updatedAt || vm.calls[0].callDateTime,
        tab: 'call-logs',
      });
    }

    if (vm.claims[0]) {
      items.push({
        id: `claim-${vm.claims[0].claimId}`,
        label: 'Claim recorded',
        detail: vm.claims[0].claimStatus || vm.claims[0].claimNumber || 'Financial update',
        date: vm.claims[0].updatedAt || vm.claims[0].submittedAt,
        tab: 'cases',
      });
    }

    if (vm.documents[0]) {
      items.push({
        id: `document-${vm.documents[0].sourceId}`,
        label: 'Document added',
        detail: vm.documents[0].fileName || 'Patient document',
        date: vm.documents[0].uploadedAt,
        tab: 'documents',
      });
    }

    return items
      .filter((item) => item.date)
      .sort((left, right) => new Date(right.date ?? '').getTime() - new Date(left.date ?? '').getTime())
      .slice(0, 4);
  }, [activeCase, vm.calls, vm.claims, vm.documents]);

  const metadataRows = React.useMemo(() => ([
    { label: 'Patient code', value: vm.patientCode, icon: FileText },
    { label: 'Enrolled', value: formatDisplayDate(vm._raw.patient.createdAt), icon: Clock },
    { label: 'Last updated', value: vm._raw.patient.updatedAt ? formatDisplayDate(vm._raw.patient.updatedAt) : '—', icon: Clock },
    { label: 'Current step', value: formatWorkflowAction(vm.currentWorkflowStepName) || 'Not available', icon: Briefcase },
  ]), [vm._raw.patient.createdAt, vm._raw.patient.updatedAt, vm.currentWorkflowStepName, vm.patientCode]);

  return (
    <aside
      className={cn(
        'hidden xl:flex flex-col w-[300px] shrink-0',
        'xl:sticky xl:top-4',
        className,
      )}
    >
      {/* Inner scroll container */}
      <div className={cn('max-h-[calc(100vh-7rem)] overflow-y-auto pr-0.5', isCompact ? 'space-y-2' : 'space-y-3')}>

        <SideCard title="Activity Timeline" icon={Clock}>
          {timelineItems.length > 0 ? (
            <div className="space-y-2">
              {timelineItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    if (item.scrollTargetId) {
                      onScrollTo?.(item.scrollTargetId);
                    }
                    if (item.tab) {
                      onNavigate?.(item.tab);
                    }
                  }}
                  className="timeline-item w-full text-left"
                >
                  <span className="timeline-dot" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] font-medium text-foreground">{item.label}</span>
                    <span className="block text-[11px] text-muted-foreground">{item.detail}</span>
                  </span>
                  <span className="timeline-date">{formatDisplayDate(item.date)}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-muted-foreground">No recent timeline activity available.</p>
          )}
        </SideCard>

        {/* ── Quick Metadata ────────────────────────────────── */}
        <SideCard title="Key Metadata" icon={Info}>
          {/* Dynamic zone (renders null when empty, so fallback shows) */}
          {patientEntity && (
            <DynamicZoneRenderer
              zoneKey="sidebar"
              entityData={patientEntity}
              renderMode="flat"
            />
          )}
          {/* Fallback: hardcoded fields shown when no dynamic data or zone is empty */}
          {!patientEntity && (
            <div className="space-y-1.5">
              {metadataRows.map((row) => (
                <MetaRow key={row.label} label={row.label} value={row.value} icon={row.icon} />
              ))}
              <MetaRow label="Hospital" value={vm.hospitalName} icon={Building2} />
              <MetaRow label="Doctor / Hospital" value={vm.hcpName} icon={Stethoscope} />
            </div>
          )}
        </SideCard>

        {alerts.length > 0 && (
          <SideCard title="Alerts / Flags" icon={AlertTriangle}>
            <div className="space-y-2">
              {alerts.map((alert) => (
                <button
                  key={alert.id}
                  type="button"
                  onClick={() => {
                    if (alert.scrollTargetId) {
                      onScrollTo?.(alert.scrollTargetId);
                    }
                    if (alert.targetTab) {
                      onNavigate?.(alert.targetTab as Patient360NavKey);
                    }
                  }}
                  className="alert-flag-item"
                >
                  <PremiumBadge size="sm" variant={alert.severity === 'critical' ? 'error' : alert.severity === 'warning' ? 'warning' : 'info'}>
                    {alert.severity === 'critical' ? 'High' : alert.severity === 'warning' ? 'Watch' : 'Info'}
                  </PremiumBadge>
                  <span className="text-[11px] font-medium text-foreground">{alert.message}</span>
                  {alert.detail ? <span className="block text-[11px] text-muted-foreground">{alert.detail}</span> : null}
                </button>
              ))}
            </div>
          </SideCard>
        )}

      </div>
    </aside>
  );
});
